import { motion } from 'framer-motion';
import { useState } from 'react';
import ScrollTypeWriter from './ScrollTypeWriter';
import { softwareCategories } from '../data/softwareWeUse';

const SoftwareLogo = ({ item }) => {
  const [imgFailed, setImgFailed] = useState(false);

  // Render lettermark badge if no icon provided or img load failed
  if (!item.icon || imgFailed) {
    return (
      <div
        className="w-14 h-14 rounded-lg flex items-center justify-center font-black text-white text-sm tracking-tight shadow-md"
        style={{ background: item.color || '#02d871' }}
        aria-label={item.name}
      >
        {item.lettermark || item.name.slice(0, 2).toUpperCase()}
      </div>
    );
  }

  return (
    <div className="w-14 h-14 rounded-lg bg-white border border-border flex items-center justify-center p-2 shadow-sm">
      <img
        src={item.icon}
        alt={item.name}
        loading="lazy"
        className="max-w-full max-h-full object-contain"
        onError={() => setImgFailed(true)}
       width="1200" height="800" />
    </div>
  );
};

export default function SoftwareWeUse({ variant = 'light', compact = false }) {
  const [activeCat, setActiveCat] = useState(softwareCategories[0].id);
  const active = softwareCategories.find((c) => c.id === activeCat);

  const isDark = variant === 'dark';
  const sectionBg = isDark ? 'bg-[#0A0A0A]' : 'bg-[#f8faf8]';
  const cardBg = isDark ? 'bg-white/5 border-white/10 hover:border-primary' : 'bg-white border-border hover:border-primary';
  const textCol = isDark ? 'text-white' : 'text-foreground';
  const subCol = isDark ? 'text-white/60' : 'text-muted-foreground';

  return (
    <section className={`${compact ? 'py-16' : 'section-spacing'} ${sectionBg} relative`} data-testid="software-we-use">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-xs uppercase tracking-widest text-primary font-bold">Software We Use</span>
          <h2 className={`text-3xl md:text-4xl font-black uppercase mt-3 mb-4 ${textCol}`}>
            <ScrollTypeWriter segments={[{ text: 'Industry-Standard ', cls: '' }, { text: 'CAD Stack', cls: 'text-secondary italic' }]} />
          </h2>
          <p className={subCol}>From AutoCAD and Revit to ArcGIS and Terramodel — our team works across {softwareCategories.flatMap((c) => c.items).length}+ professional platforms.</p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" role="tablist">
          {softwareCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCat(cat.id)}
              role="tab"
              aria-selected={activeCat === cat.id}
              data-testid={`software-tab-${cat.id}`}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all border-2
                ${activeCat === cat.id
                  ? 'bg-primary text-white border-primary'
                  : isDark
                    ? 'border-white/20 text-white/70 hover:border-primary hover:text-primary'
                    : 'border-border text-foreground/70 hover:border-primary hover:text-primary'}`}
            >
              {cat.title}
            </button>
          ))}
        </div>

        {/* Active category */}
        {active && (
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className={`text-center text-sm ${subCol} mb-8 max-w-2xl mx-auto`}>{active.desc}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {active.items.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03 }}
                  className={`group flex items-center gap-3 p-4 border ${cardBg} transition-colors`}
                  data-testid={`software-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <SoftwareLogo item={item} />
                  <div className="min-w-0 flex-1">
                    <p className={`font-bold text-sm leading-tight ${textCol}`}>{item.name}</p>
                    <p className={`text-[10px] uppercase tracking-widest mt-0.5 ${subCol}`}>{active.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
